import type React from "react";
import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  AddRounded,
  ArrowBackRounded,
  DeleteOutlineRounded,
  EditOutlined,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import apiService from "../../../../service/Api";
import CustomTextField from "../../../../components/ui/Textfield";
import useDebounce from "../../../../hooks/useDebounce";
import RoleFormDialog, { type RegistryRole } from "./RoleFormDialog";

interface PackageCrewRequirementRow {
  id: number;
  role: number;
  role_name: string;
  package_name: string;
}

const RequirementsRegistryPage: React.FC = () => {
  const navigate = useNavigate();
  const [roles, setRoles] = useState<RegistryRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 400);

  const [usageByRole, setUsageByRole] = useState<Record<number, number>>({});

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<RegistryRole | null>(null);

  const fetchRoles = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, unknown> = { page_size: 200 };
      if (debouncedSearch) params.search = debouncedSearch;
      const res = await apiService.get<{ results: RegistryRole[] }>(
        "/api/crews/roles/",
        params,
      );
      setRoles(res.data.results ?? []);
    } catch {
      toast.error("Failed to load roles.");
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch]);

  const fetchUsage = useCallback(async () => {
    try {
      const res = await apiService.get<{
        results: PackageCrewRequirementRow[];
      }>("/api/automation/package-crew-requirements/", { page_size: 500 });
      const counts: Record<number, number> = {};
      (res.data.results ?? []).forEach((row) => {
        counts[row.role] = (counts[row.role] ?? 0) + 1;
      });
      setUsageByRole(counts);
    } catch {
      // usage is informational only
    }
  }, []);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  useEffect(() => {
    fetchUsage();
  }, [fetchUsage]);

  const handleDelete = async (role: RegistryRole) => {
    if (usageByRole[role.id]) {
      toast.error(
        `“${role.name}” is used by ${usageByRole[role.id]} package requirement(s). Remove those first.`,
      );
      return;
    }
    if (!window.confirm(`Delete role “${role.name}”?`)) return;
    try {
      await apiService.delete(`/api/crews/roles/${role.id}/`);
      toast.success("Role deleted.");
      fetchRoles();
    } catch {
      toast.error("Failed to delete role.");
    }
  };

  return (
    <Box sx={{ maxWidth: 960, mx: "auto", px: 3, py: 3 }}>
      <Button
        startIcon={<ArrowBackRounded />}
        onClick={() => navigate("/configurations/package")}
        sx={{ textTransform: "none", color: "text.secondary", mb: 1 }}
      >
        Back to Packages
      </Button>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          mb: 1,
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800 }}>
            Requirements Registry
          </Typography>
          <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
            The catalogue of crew roles that packages draw from when staffing
            streams.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddRounded />}
          onClick={() => {
            setEditingRole(null);
            setDialogOpen(true);
          }}
          sx={{
            textTransform: "none",
            backgroundColor: "#6b4226",
            "&:hover": { backgroundColor: "#5a3720" },
          }}
        >
          New Role
        </Button>
      </Box>

      <CustomTextField
        placeholder="Search roles..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ maxWidth: 320, my: 2 }}
        size="small"
      />

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Role</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Used by</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {roles.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4, color: "text.secondary" }}>
                  No roles found.
                </TableCell>
              </TableRow>
            )}
            {roles.map((role) => (
              <TableRow key={role.id} hover>
                <TableCell sx={{ fontWeight: 600 }}>{role.name}</TableCell>
                <TableCell sx={{ color: "text.secondary" }}>
                  {role.description || "—"}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={`${usageByRole[role.id] ?? 0} package${
                      (usageByRole[role.id] ?? 0) === 1 ? "" : "s"
                    }`}
                    sx={{ height: 20, fontSize: 11 }}
                  />
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Edit">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setEditingRole(role);
                        setDialogOpen(true);
                      }}
                    >
                      <EditOutlined fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton size="small" onClick={() => handleDelete(role)}>
                      <DeleteOutlineRounded fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <RoleFormDialog
        open={dialogOpen}
        role={editingRole}
        onClose={() => setDialogOpen(false)}
        onSaved={() => {
          fetchRoles();
          fetchUsage();
        }}
      />
    </Box>
  );
};

export default RequirementsRegistryPage;
